import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/api";
import { useLang } from "@/i18n";

export default function Announcements() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", audience: "all" });
  const [lastLinks, setLastLinks] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/announcements");
    setItems(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/announcements", form);
      toast.success(t("announcementSent"));
      setLastLinks(r.data.wa_links);
      setForm({ title: "", message: "", audience: "all" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="announcements-page">
      <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("announcements")}</h1>

      <form onSubmit={send} className="gfa-card space-y-4 p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>{t("title")} *</Label>
            <Input data-testid="ann-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label>{t("audience")}</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger data-testid="ann-audience" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allAudience")}</SelectItem>
                <SelectItem value="parents">{t("parentsOnly")}</SelectItem>
                <SelectItem value="staff">{t("staffOnly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>{t("message")} *</Label>
          <Textarea data-testid="ann-message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" />
        </div>
        <Button data-testid="ann-send-btn" disabled={busy} className="rounded-full bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]">
          <Megaphone size={15} className="mr-1" /> {busy ? t("loading") : t("send")}
        </Button>
      </form>

      {lastLinks && (
        <div className="gfa-card border-[#84CC16] p-6" data-testid="wa-links-panel">
          <p className="mb-3 font-['Outfit'] font-bold text-[#165A2E]">{t("waLinks")} ({lastLinks.length} {t("recipientCount")})</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {lastLinks.map((l) => (
              <a key={l.phone + l.name} href={l.url} target="_blank" rel="noreferrer" data-testid={`wa-link-${l.phone}`}
                className="flex items-center gap-2 rounded-lg border border-slate-100 p-3 text-sm font-semibold text-[#25D366] transition-colors duration-200 hover:bg-[#25D366] hover:text-white">
                <MessageCircle size={15} /> {l.name} · {l.phone}
              </a>
            ))}
            {lastLinks.length === 0 && <p className="text-sm text-slate-400">{t("noData")}</p>}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.announcement_id} className="gfa-card p-5" data-testid={`ann-${a.announcement_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-['Outfit'] font-bold text-slate-800">{a.title}</h3>
              <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()} · {a.recipient_count} {t("recipientCount")}</span>
            </div>
            <p className="mt-2 whitespace-pre-line text-sm text-slate-600">{a.message}</p>
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-semibold text-[#165A2E]">{t("waLinks")}</summary>
              <div className="mt-2 flex flex-wrap gap-2">
                {(a.wa_links || []).map((l) => (
                  <a key={l.phone + l.name} href={l.url} target="_blank" rel="noreferrer" className="rounded-full bg-[#25D366]/10 px-3 py-1 text-xs font-semibold text-[#1da851] hover:bg-[#25D366] hover:text-white">
                    {l.name}
                  </a>
                ))}
              </div>
            </details>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-400">{t("noData")}</p>}
      </div>
    </div>
  );
}
